import { Component, h, Host, State } from '@stencil/core';
import { orderConverter, paymentMethodConverter } from '../../utils/firebase';
import { collection, collectionGroup, deleteDoc, deleteField, doc, getDocs, orderBy, query, updateDoc, where } from '@firebase/firestore';
import spacetime from 'spacetime';

import { FirebaseService } from '../../utils/firebase';
import { settings } from '../../store/settings.store';
import { customer } from '../../store/customer.store';

import { Order } from '@artask/tambr-models/models/orders.model';
import { PlatformCustomerPaymentMethod } from '../../components';


@Component({
  tag: 'tambr-account-wallet',
  styleUrl: 'tambr-account-wallet.css',
  shadow: false,
})
export class TambrAccountWallet {

  @State() paymentMethods: PlatformCustomerPaymentMethod[] = [];
  @State() paymentMethodsLoading: boolean = false;
  @State() paymentMethodsLoaded: boolean = false;

  @State() upcomingOrders: Order[] = [];
  @State() upcomingOrdersLoading: boolean = false;
  @State() upcomingOrdersLoaded: boolean = false;

  @State() error: string | null = null;

  paymentMethodToDelete: PlatformCustomerPaymentMethod;
  confirmationDialogEl: HTMLTambrConfirmationDialogElement;
  paymentMethodAddDialogEl: HTMLTambrPaymentMethodAddElement;
  messageEl: HTMLTambrMessageElement;

  componentDidLoad() { 
    this.loadCustomerPaymentMethods();
    this.loadUpcomingOrders();
  }

  disconnectedCallback() {
    this.closePaymentMethodAddDialog();
    this.closeConfirmationDialog(); 
  }

  async loadCustomerPaymentMethods() {
    this.paymentMethodsLoading = true;
    try {
      const paymentMethodsCollectionQuery = query(
        collection( FirebaseService.firestore, `users/${customer.platformCustomer.uid}/paymentMethods` ),
        orderBy( 'created', 'desc' )
      ).withConverter( paymentMethodConverter );
      const paymentMethodsCollection = await getDocs( paymentMethodsCollectionQuery );
      this.paymentMethods = paymentMethodsCollection.docs.map( doc => ({ id: doc.id, ...doc.data() } as PlatformCustomerPaymentMethod) );
    } catch(e) {
      console.log(e);
      this.error = e.message;
    }
    this.paymentMethodsLoaded = true;
    this.paymentMethodsLoading = false;
  }

  async loadUpcomingOrders() {
    this.upcomingOrdersLoading = true;
    try {
      const ordersCollectionQuery = query(
        collectionGroup( FirebaseService.firestore, 'orders' ),
        where( 'customer.email', '==', customer.platformCustomer.email ),
        where( 'status', 'in', ['confirmed', 'active'] ),
        orderBy( 'startTime', 'desc' )
      ).withConverter(orderConverter);
      const ordersCollection = await getDocs( ordersCollectionQuery );
      const tz = settings.businessSettings.timezone;
      this.upcomingOrders = ordersCollection.docs.map( doc => doc.data() ).filter( order => {
        return spacetime( order?.paymentScheduledFor?.toMillis(), tz ).isAfter( spacetime.now( tz ) )
      });
    } catch(e) {
      console.log(e);
      this.error = e.message;
    }
    this.upcomingOrdersLoaded = true;
    this.upcomingOrdersLoading = false;
  }

  openPaymentMethodAddDialog() {
    if (document.getElementsByTagName('tambr-payment-method-add').length) return;    
    this.paymentMethodAddDialogEl = document.createElement('tambr-payment-method-add');
    this.paymentMethodAddDialogEl.addEventListener( 'tambrPaymentMethodAddSuccess', () => { this.handlePaymentMethodAddSuccess() });
    this.paymentMethodAddDialogEl.addEventListener( 'tambrPaymentMethodAddClose', () => { this.closePaymentMethodAddDialog() });
    document.body.appendChild(this.paymentMethodAddDialogEl);
  }

  handlePaymentMethodAddSuccess() {
    this.loadCustomerPaymentMethods();
    this.closePaymentMethodAddDialog();
  }

  closePaymentMethodAddDialog() {
    if (document.getElementsByTagName('tambr-payment-method-add').length) {
      document.body.removeChild(this.paymentMethodAddDialogEl);
    }
  }

  async deletePaymentMethod(paymentMethod: PlatformCustomerPaymentMethod) {
    try {
      if ( this.checkUpcomingOrder(paymentMethod) ) {
        this.showPreventingMessage();
      } else {
        if (document.getElementsByTagName('tambr-confirmation-dialog').length) return;
        this.paymentMethodToDelete = paymentMethod;
        this.confirmationDialogEl = document.createElement('tambr-confirmation-dialog');
        this.confirmationDialogEl.addEventListener( 'tambrConfirmationDialogResponse', (response: any) =>    
          this.handleDeletePaymentMethodConfirmationResponse(response.detail) );
        this.confirmationDialogEl.message = `Would you like to delete the card ending ${paymentMethod.last4}?`;
        this.confirmationDialogEl.confirmText = 'Delete Card';
        this.confirmationDialogEl.cancelText = 'Cancel';
        document.body.appendChild(this.confirmationDialogEl);
      }
    } catch (e) {
      console.log(e);
      this.error = e.message;
    }
  }

  checkUpcomingOrder( paymentMethod: PlatformCustomerPaymentMethod ): boolean {
    return this.upcomingOrders.some( order => 
      order?.paymentMethodDetails?.brand === paymentMethod.brand &&
      order?.paymentMethodDetails?.last4 === paymentMethod.last4 &&
      order?.paymentMethodDetails?.expMonth === paymentMethod.expMonth &&
      order?.paymentMethodDetails?.expYear === paymentMethod.expYear
    )
  }

  showPreventingMessage() {
    this.messageEl = document.createElement('tambr-message');
    this.messageEl.header = 'Payment method can not be removed';
    this.messageEl.message = "This payment method is associated with some of your upcoming bookings. Please update the payment method in all bookings before removing this payment method.";
    this.messageEl.addEventListener( 'tambrMessageClose', () => this.messageEl.remove() );
    document.body.appendChild(this.messageEl);
  }

  showErrorMessage(message: string) {
    this.messageEl = document.createElement('tambr-message');
    this.messageEl.header = 'Error';
    this.messageEl.message = message;
    this.messageEl.addEventListener( 'tambrMessageClose', () => this.messageEl.remove() );
    document.body.appendChild(this.messageEl);
  }

  async handleDeletePaymentMethodConfirmationResponse(response: string) {
    if (response === 'confirm') {
      this.confirmationDialogEl.loading = true;
      try {
        const paymentMethodId = this.paymentMethodToDelete.id;
        const paymentMethodDocRef = doc(FirebaseService.firestore, `users/${customer.platformCustomer.uid}/paymentMethods/${paymentMethodId}`);
        await deleteDoc(paymentMethodDocRef);
        await this.loadCustomerPaymentMethods();
        if (customer.platformCustomer?.stripeDefaultPaymentMethodId === paymentMethodId) {
          this.setDefaultPaymentMethod( this.paymentMethods[0] ? this.paymentMethods[0].id : null );
        }
      } catch (e) {
        console.log(e);
        this.error = e.message;
      }
    }
    this.closeConfirmationDialog();       
  }

  async setDefaultPaymentMethod( paymentMethodId: string | null ) {
    this.paymentMethodsLoading = true;
    try {
      const userDocRef = doc( FirebaseService.firestore, `users/${customer.platformCustomer.uid}` );
      await updateDoc( userDocRef, { stripeDefaultPaymentMethodId: paymentMethodId ?? deleteField() } );
    } catch(e) {
      console.log(e);
      this.error = e.message;
    }
    this.paymentMethodsLoading = false;
  }

  closeConfirmationDialog() {
    if ( document.getElementsByTagName('tambr-confirmation-dialog').length ) {
      document.body.removeChild(this.confirmationDialogEl); 
    }
  }

  render() {
    return (
      <Host>
        { this.error }
        { this.paymentMethodsLoaded && this.upcomingOrdersLoaded
          ? this.renderMainContent()            
          : this.renderPlaceholder() 
        }
      </Host>
    );
  }

  renderMainContent() {
    return([
      <div class={'loading-overlay' + ( this.paymentMethodsLoading || this.upcomingOrdersLoading  ? ' open' : '' ) }>
        <tambr-loading-spinner />
      </div>,

      this.renderCredit(),

      this.renderPaymentMethods(),

      <div class='tambr-action-buttons'>
        <div class='tambr-button' onClick={ () => this.openPaymentMethodAddDialog() }>Add Card</div>
      </div>
    ]);
  }

  renderCredit() {
    if ( customer.businessCustomer?.credit ) {
      return (
        <div class='credit-wrapper'>
          <div class='credit-title'>Available Credit</div>
          <div class='credit'>{ settings.businessSettings.businessName }: <strong>${ (customer.businessCustomer?.credit / 100).toFixed(2) }</strong></div>
        </div>      
      );
    }
  }

  renderPaymentMethods() {
    if ( !this.paymentMethods.length ) return (
      <div class='no-payment-methods'>You don't have any saved payment methods yet.</div>
    );

    return (
      <div class='payment-methods-wrapper'>
        <div class='payment-methods-title'>Saved Cards</div>
        { this.paymentMethods.map( paymentMethod => this.renderPaymentMethod( paymentMethod ) ) }
      </div>
    );
  }

  renderPaymentMethod( paymentMethod: PlatformCustomerPaymentMethod ) {
    return (
      <div class='payment-method'>
        <tambr-payment-method paymentMethod={paymentMethod} />
        <div class='payment-method-action-buttons'>{ this.renderDeleteButton(paymentMethod) }</div>
      </div>
    );
  }

  renderDeleteButton( paymentMethod: PlatformCustomerPaymentMethod ) {
    if (this.upcomingOrdersLoading) return;
    return (
      <span class='payment-method-action-button payment-method-delete' 
        onClick={ () => this.deletePaymentMethod(paymentMethod) }>Delete</span>
    )
  }

  renderPlaceholder() {
    return ([
      <div class='skeleton-card'>
        <div class='card-info'>
          <span class='skeleton-text card-number' />
          <span class='skeleton-text card-expiry' />
        </div>
        <div class='card-actions'>
          <span class='skeleton-text'/>
          <span class='skeleton-text red'/>
        </div>
      </div>,
      <div class='skeleton-card'>
        <div class='card-info'>
          <span class='skeleton-text card-number' />
          <span class='skeleton-text card-expiry' />
        </div>
        <div class='card-actions'>
          <span class='skeleton-text'/>
          <span class='skeleton-text red'/>
        </div>
      </div>
    ]);
  }

}
